import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  BrandingFactors,
  DEFAULT_BRANDING_CONFIGURATION,
} from '../models/configuration';
import { loadBrandingFactors } from '../api/configuration';

interface BrandingContextType {
  branding: BrandingFactors;
  loading: boolean;
  refresh: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined
);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

interface BrandingProviderProps {
  children: ReactNode;
  organizationId: string | null;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
  organizationId,
}) => {
  const [branding, setBranding] = useState<BrandingFactors>(
    DEFAULT_BRANDING_CONFIGURATION
  );
  const [loading, setLoading] = useState(false);

  // Branding is logo-only for now: load and expose logo, but do not apply any colors/fonts to the UI.
  useEffect(() => {
    let cancelled = false;

    const loadLogoOnly = async () => {
      if (!organizationId) {
        setBranding(DEFAULT_BRANDING_CONFIGURATION);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await loadBrandingFactors(organizationId);
        if (cancelled) return;
        setBranding({
          ...DEFAULT_BRANDING_CONFIGURATION,
          logo: data.logo || '',
          logoStoragePath: data.logoStoragePath,
        });
      } catch (error) {
        console.error('Failed to load branding (logo-only):', error);
        if (!cancelled) {
          setBranding(DEFAULT_BRANDING_CONFIGURATION);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLogoOnly();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const refresh = async () => {
    if (!organizationId) {
      setBranding(DEFAULT_BRANDING_CONFIGURATION);
      return;
    }

    try {
      const data = await loadBrandingFactors(organizationId);
      setBranding({
        ...DEFAULT_BRANDING_CONFIGURATION,
        logo: data.logo || '',
        logoStoragePath: data.logoStoragePath,
      });
    } catch (error) {
      console.error('Failed to refresh branding (logo-only):', error);
      setBranding(DEFAULT_BRANDING_CONFIGURATION);
    }
  };

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
};
